import { BasicButton, BasicModal, Typography } from "@/components";
import { BackAppBar } from "@/components/ui";
import { useBoolean } from "@/hooks/useBoolean";
import useChannel from "@/hooks/useChannel";
import useCloseGroup from "@/hooks/useCloseGroup";
import { formatDate } from "@/libraries/date";
import { genChannelName } from "@/libraries/helper";
import { getGroupQueryOptions, getMemberQueryOptions } from "@/queries";
import type { GroupMember } from "@/types";
import AddIcon from "@mui/icons-material/Add";
import ArchiveIcon from "@mui/icons-material/Archive";
import ChatOutlinedIcon from "@mui/icons-material/ChatOutlined";
import CloseIcon from "@mui/icons-material/Close";
import CreateIcon from "@mui/icons-material/Create";
import DoDisturbAltIcon from "@mui/icons-material/DoDisturbAlt";
import PersonOutlineIcon from "@mui/icons-material/PersonOutline";
import { Divider, IconButton } from "@mui/material";
import Avatar from "@mui/material/Avatar";
import Button from "@mui/material/Button";
import Drawer from "@mui/material/Drawer";
import List from "@mui/material/List";
import ListItem from "@mui/material/ListItem";
import ListItemAvatar from "@mui/material/ListItemAvatar";
import ListItemButton from "@mui/material/ListItemButton";
import ListItemText from "@mui/material/ListItemText";
import { useQuery, useSuspenseQuery } from "@tanstack/react-query";
import { Link, createFileRoute } from "@tanstack/react-router";
import { useState } from "react";
import { toast } from "sonner";

export const Route = createFileRoute("/dashboard/chat/group/$id/detail")({
	component: RouteComponent,
});

interface GroupBaseInfoProps {
	label: string;
	value?: string | null;
}

function GroupBaseInfo(props: GroupBaseInfoProps) {
	return (
		<div className="flex flex-col gap-1 justify-center items-start">
			<Typography fontWeight={400} fontSize={14} className="text-zinc-700">
				{props.label}
			</Typography>
			<Typography fontWeight={500} fontSize={14} className="text-zinc-700">
				{props.value || "-"}
			</Typography>
		</div>
	);
}

function RouteComponent() {
	const navigate = Route.useNavigate();
	const groupId = Route.useParams({
		select: (params) => params.id,
	});
	const {
		value: isOpenConfirmDelete,
		setTrue: openConfirmDelete,
		setFalse: closeConfirmDelete,
	} = useBoolean();
	const [viewMember, setViewMember] = useState<GroupMember | null>(null);
	const { mutateCreateChannel } = useChannel();
	const { data: resGroup } = useSuspenseQuery(getGroupQueryOptions(groupId));
	const memberQuery = useQuery(getMemberQueryOptions(viewMember?.id));
	const isClosed = resGroup.group.status === "closed";
	const { mutate: mutateCloseGroup } = useCloseGroup();
	const onConfirmCloseGroup = (e: React.MouseEvent<HTMLButtonElement>) => {
		e.preventDefault();
		return mutateCloseGroup(groupId, {
			onSuccess: () => {
				toast.success("Group closed");
				return closeConfirmDelete();
			},
			onError: () => {
				return toast.error("Failed to close group");
			},
		});
	};
	const onAddMembers = (e: React.MouseEvent<HTMLLIElement>) => {
		e.preventDefault();
		return navigate({
			to: "/dashboard/chat/group/$id/members",
			params: { id: groupId },
		});
	};

	const onClickMessage = async (e: React.MouseEvent<HTMLDivElement>) => {
		if (!memberQuery.data) return;
		e.preventDefault();
		let channelName = memberQuery.data.user.channel_name;
		if (!channelName) {
			const resChannelName = await mutateCreateChannel.mutateAsync({
				name: genChannelName("single"),
				messageable_id: memberQuery.data.user.id.toString(),
				messageable_type: "User",
			});
			channelName = String(resChannelName.user.channel_name);
		}
		return navigate({
			to: "/dashboard/chat/single/$id/$channelName",
			params: {
				id: memberQuery.data.user.id,
				channelName,
			},
		});
	};

	return (
		<div className="relative bg-gray-light min-h-full">
			<div className="bg-gradient-chat">
				<BackAppBar
					className="[&_*_]:text-white"
					bgColor="transparent"
					title="Group Info"
				/>
			</div>
			<div className="flex flex-col gap-2">
				<div className="container py-4 bg-white space-y-3">
					<div className="flex flex-col gap-2 justify-center items-start">
						<Typography fontWeight={700} fontSize={14}>
							Group Profile
						</Typography>
						<Avatar
							alt={resGroup.group.name}
							className="!bg-gray-light !w-20 !h-20 !text-dark"
							src={resGroup.group.avatar ?? "/broken-image.jpg"}
						/>
					</div>
					<GroupBaseInfo label="Group name" value={resGroup.group.name} />
					<div className="grid grid-cols-2">
						<GroupBaseInfo
							label="Created by"
							value={`${resGroup.group.user.first_name} ${resGroup.group.user.last_name}`}
						/>
						<GroupBaseInfo
							label="Date of Creation"
							value={formatDate(resGroup.group.created_at)}
						/>
					</div>
					<GroupBaseInfo
						label="Organization"
						value={resGroup.group.organization}
					/>
					<GroupBaseInfo
						label="Group Synopsis"
						value={resGroup.group.synopsis}
					/>
					<GroupBaseInfo
						label="Group Location Description"
						value={resGroup.group.location}
					/>
					<Link
						to="/dashboard/chat/group/$id/edit"
						params={{ id: resGroup.group.id }}
						preload={false}
					>
						<Button
							variant="contained"
							color="primary"
							className="!rounded-full float-end"
							startIcon={<CreateIcon />}
						>
							Edit
						</Button>
					</Link>
				</div>
				<div className="container py-4 bg-white space-y-3">
					<Typography fontWeight={700} fontSize={17}>
						Group Members ({resGroup.group.members.length})
					</Typography>
					<List className="divide-y divide-gray-light">
						<ListItem disablePadding onClick={onAddMembers}>
							<ListItemButton>
								<ListItemAvatar>
									<Avatar className="!bg-primary-light !text-primary">
										<AddIcon />
									</Avatar>
								</ListItemAvatar>
								<ListItemText className="text-primary" primary="ADD MEMBERS" />
							</ListItemButton>
						</ListItem>
						{resGroup.group.members.map((member) => (
							<ListItem
								key={member.id}
								disablePadding
								secondaryAction={
									member.id === resGroup.group.user.id ? (
										<div className="bg-primary-light px-2 py-1 rounded-md">
											<Typography
												fontWeight={500}
												fontSize={12}
												className="text-primary"
											>
												Owner
											</Typography>
										</div>
									) : null
								}
							>
								<ListItemButton
									onClick={() =>
										member.id !== resGroup.group.user.id
											? setViewMember(member)
											: null
									}
								>
									<ListItemAvatar>
										<Avatar
											alt={member.first_name || "L"}
											className="!bg-gray-light !text-dark"
											src={member.avatar ?? "/broken-image.jpg"}
										/>
									</ListItemAvatar>
									<ListItemText primary={member.fullname} />
								</ListItemButton>
							</ListItem>
						))}
					</List>
				</div>
				<div className="container py-4 bg-white space-y-3">
					<Typography fontWeight={700} fontSize={17}>
						Pending Acceptance ({resGroup.group.pending_invitations.length})
					</Typography>
					<List className="divide-y divide-gray-light">
						{resGroup.group.pending_invitations.map((member) => (
							<ListItem
								key={member.id}
								disablePadding
								secondaryAction={
									<div className="bg-gray-light px-2 py-1 rounded-md">
										<Typography
											fontWeight={500}
											fontSize={12}
											className="text-zinc-700"
										>
											Invited
										</Typography>
									</div>
								}
							>
								<ListItemButton>
									<ListItemAvatar>
										<Avatar
											alt={member.first_name || "L"}
											className="!bg-gray-light !text-dark"
											src={member.avatar ?? "/broken-image.jpg"}
										/>
									</ListItemAvatar>
									<ListItemText primary={member.fullname} />
								</ListItemButton>
							</ListItem>
						))}
					</List>
				</div>
				<div className="container py-4 bg-white space-y-3">
					<BasicButton
						fullWidth
						variant="outlined"
						color={isClosed ? "inherit" : "error"}
						startIcon={isClosed ? <DoDisturbAltIcon /> : <ArchiveIcon />}
						onClick={openConfirmDelete}
						disabled={isClosed}
					>
						{isClosed ? "Group Closed" : "Close This Group"}
					</BasicButton>
				</div>
			</div>
			<BasicModal isOpen={isOpenConfirmDelete} onClose={closeConfirmDelete}>
				<div className="flex flex-col justify-center items-center gap-3">
					<Typography fontWeight={600} fontSize={16}>
						Close Group?
					</Typography>
					<Typography fontWeight={400} fontSize={14} align="center">
						Are you sure you wish to close this group? You won’t be able to post
						and open this group anymore.
					</Typography>
					<Button
						size="large"
						variant="contained"
						color="error"
						className="!rounded-full"
						fullWidth
						onClick={onConfirmCloseGroup}
					>
						Yes, Close Group
					</Button>
					<Button
						size="large"
						variant="outlined"
						color="primary"
						className="!rounded-full"
						onClick={closeConfirmDelete}
						fullWidth
					>
						Cancel
					</Button>
				</div>
			</BasicModal>
			<Drawer
				open={!!viewMember}
				onClose={() => setViewMember(null)}
				anchor="bottom"
				classes={{
					paper: "rounded-t-2xl",
				}}
			>
				<div className="wrapper bg-white py-4">
					<div className="container">
						<div className="flex justify-between items-center">
							<Typography fontSize={16} fontWeight={600}>
								{viewMember?.fullname}
							</Typography>
							<IconButton
								color="primary"
								size="medium"
								onClick={() => setViewMember(null)}
							>
								<CloseIcon />
							</IconButton>
						</div>
					</div>
					<Divider />
					<div className="container">
						<List>
							<ListItem disablePadding>
								<ListItemButton sx={{ px: 1 }} onClick={onClickMessage}>
									<ListItemAvatar>
										<Avatar className="!bg-primary-light !text-primary">
											<ChatOutlinedIcon />
										</Avatar>
									</ListItemAvatar>
									<ListItemText primary={`Chat ${viewMember?.fullname}`} />
								</ListItemButton>
							</ListItem>
							<Divider />
							<ListItem disablePadding>
								<ListItemButton
									sx={{ px: 1 }}
									onClick={() =>
										navigate({
											to: "/dashboard/contacts/member/$id",
											params: { id: viewMember?.id },
										})
									}
								>
									<ListItemAvatar>
										<Avatar className="!bg-primary-light !text-primary">
											<PersonOutlineIcon />
										</Avatar>
									</ListItemAvatar>
									<ListItemText primary={`View ${viewMember?.fullname}`} />
								</ListItemButton>
							</ListItem>
						</List>
					</div>
				</div>
			</Drawer>
		</div>
	);
}
